import { Box, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import IEmailNotification from '../../interfaces/IEmailNotification';
import CAppFormKeyboardDatePicker from '../../../../app/components/Form/CAppFormKeyboardDatePicker';
import CAppFormTextField from '../../../../app/components/Form/CAppFormTextField';
import langAdminEmailNotifications from '../../langs/langAdminEmailNotifications';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import _ from 'lodash';
import HDocumentFile from '../../../document/helpers/HDocumentFile';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import CAppFilterSelect from '../../../../app/components/CAppFilterSelect';
import IFund from '../../../investor/interfaces/fund/IFund';
import IInvestorGroup from '../../../investor/interfaces/IInvestorGroup';

type CAdminEmailNotificationFiltersProps = {
	emailNotifications: IEmailNotification[];
	onChange: (values: IEmailNotification[]) => void;
	hideFilters?: string[];
};

const CAdminEmailNotificationFilters = (props: CAdminEmailNotificationFiltersProps) => {
	const { emailNotifications, onChange, hideFilters } = props;

	const [filters, setFilters] = useState<{
		startReleaseDate: Date;
		endReleaseDate: Date;
		documentKeywords: string;
		contactKeywords: string;
		fundIds: number[] | null;
		groupIds: number[] | null;
	}>({
		startReleaseDate: moment().subtract(1, 'month').toDate(),
		endReleaseDate: moment().add(1, 'day').toDate(),
		documentKeywords: '',
		contactKeywords: '',
		fundIds: null,
		groupIds: null,
	});
	const { t } = useTranslation();

	const [funds, setFunds] = useState<IFund[]>([]);
	const [groups, setGroups] = useState<IInvestorGroup[]>([]);
	useEffect(() => {
		if (emailNotifications) {
			let emailNotificationsTmp = _.cloneDeep(emailNotifications);
			if (filters?.startReleaseDate) {
				emailNotificationsTmp = emailNotificationsTmp.filter(
					(x) =>
						moment(moment(x.documentReleaseDate).format('YYYY-MM-DD')).diff(
							moment(moment(filters.startReleaseDate).format('YYYY-MM-DD')),
						) >= 0,
				);
			}
			if (filters?.endReleaseDate) {
				emailNotificationsTmp = emailNotificationsTmp.filter(
					(x) =>
						moment(moment(x.documentReleaseDate).format('YYYY-MM-DD')).diff(
							moment(moment(filters.endReleaseDate).format('YYYY-MM-DD')),
						) <= 0,
				);
			}
			setFunds(
				_.uniqBy(
					emailNotificationsTmp.filter((x) => x.fund).map((x) => x.fund),
					'id',
				),
			);

			setGroups(
				_.uniqBy(
					emailNotificationsTmp.filter((x) => x.documentCompany).map((x) => x.documentCompany),
					'id',
				),
			);
		}
	}, [emailNotifications, filters.endReleaseDate, filters.startReleaseDate]);

	useEffect(() => {
		if (filters && emailNotifications) {
			let emailNotificationsTmp = _.cloneDeep(emailNotifications);
			if (filters.fundIds) {
				emailNotificationsTmp = emailNotificationsTmp.filter(
					(x) => x.fund && filters.fundIds?.includes(x.fund.id),
				);
			}
			if (filters.groupIds) {
				emailNotificationsTmp = emailNotificationsTmp.filter(
					(x) => x.documentCompany && filters.groupIds?.includes(x.documentCompany?.id),
				);
			}
			if (filters.startReleaseDate) {
				emailNotificationsTmp = emailNotificationsTmp.filter(
					(x) =>
						moment(moment(x.documentReleaseDate).format('YYYY-MM-DD')).diff(
							moment(moment(filters.startReleaseDate).format('YYYY-MM-DD')),
						) >= 0,
				);
			}
			if (filters.endReleaseDate) {
				emailNotificationsTmp = emailNotificationsTmp.filter(
					(x) =>
						moment(moment(x.documentReleaseDate).format('YYYY-MM-DD')).diff(
							moment(moment(filters.endReleaseDate).format('YYYY-MM-DD')),
						) <= 0,
				);
			}
			if (filters.documentKeywords) {
				const searchWords = filters.documentKeywords.split(' ');
				searchWords.forEach((word) => {
					emailNotificationsTmp = emailNotificationsTmp.filter((x) =>
						HDocumentFile.cleanWord(x.document.name).includes(HDocumentFile.cleanWord(word)),
					);
				});
			}
			if (
				(!hideFilters || (hideFilters && !hideFilters.includes('contactKeywords'))) &&
				filters.contactKeywords
			) {
				const searchWords = filters.contactKeywords.split(' ');
				searchWords.forEach((word) => {
					emailNotificationsTmp = emailNotificationsTmp.filter((x) =>
						HDocumentFile.cleanWord(x.user.email + ' ' + x.user.firstName + ' ' + x.user.lastName).includes(
							HDocumentFile.cleanWord(word),
						),
					);
				});
			}
			onChange(emailNotificationsTmp);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, emailNotifications]);

	return (
		<Box>
			<Grid container spacing={3}>
				<Grid item sm={2}>
					<CAppFormKeyboardDatePicker
						label={t(langAdminEmailNotifications.filter_start_released_date.tag)}
						value={filters?.startReleaseDate}
						inputFormat="DD/MM/YYYY"
						onChange={(obj) => setFilters((prevData) => ({ ...prevData, startReleaseDate: obj as Date }))}
						renderInput={(params) => (
							<CAppFormTextField
								{...params}
								fullWidth
								variant="outlined"
								inputProps={{
									...params.inputProps,
									placeholder: 'JJ/MM/AAAA',
									style: { textAlign: 'center' },
								}}
							/>
						)}
					/>
				</Grid>
				<Grid item sm={2}>
					<CAppFormKeyboardDatePicker
						label={t(langAdminEmailNotifications.filter_end_released_date.tag)}
						value={filters?.endReleaseDate}
						inputFormat="DD/MM/YYYY"
						onChange={(obj) => setFilters((prevData) => ({ ...prevData, endReleaseDate: obj as Date }))}
						renderInput={(params) => (
							<CAppFormTextField
								{...params}
								fullWidth
								variant="outlined"
								inputProps={{
									...params.inputProps,
									placeholder: 'JJ/MM/AAAA',
									style: { textAlign: 'center' },
								}}
							/>
						)}
					/>
				</Grid>
				<Grid item sm={2}>
					<CAppFilterSelect
						multiple
						multipleRenderTags={(val: IFund[]) => (
							<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
								{val ? val.length : 0} fund(s) selected
							</Box>
						)}
						value={
							funds
								? funds.filter((x) => filters && filters.fundIds && filters.fundIds.includes(x.id))
								: null
						}
						onChange={(val: IFund[]) => {
							setFilters((prevData) => ({
								...prevData,
								fundIds: val && val.length > 0 ? val.map((x) => x.id) : null,
							}));
						}}
						label={t(langAdminEmailNotifications.filter_funds.tag)}
						size="small"
						options={
							funds
								? funds.map((x: IFund) => ({
										id: x.id,
										name: x.name,
								  }))
								: null
						}
					/>
				</Grid>
				<Grid item sm={2}>
					<CAppFilterSelect
						multiple
						multipleRenderTags={(val: IInvestorGroup[]) => (
							<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
								{val ? val.length : 0} group(s) selected
							</Box>
						)}
						value={
							groups
								? groups.filter((x) => filters && filters.groupIds && filters.groupIds.includes(x.id))
								: null
						}
						onChange={(val: IInvestorGroup[]) => {
							setFilters((prevData) => ({
								...prevData,
								groupIds: val && val.length > 0 ? val.map((x) => x.id) : null,
							}));
						}}
						label={t(langAdminEmailNotifications.filter_groups.tag)}
						size="small"
						options={
							groups
								? groups.map((x: IInvestorGroup) => ({
										id: x.id,
										name: x.name,
								  }))
								: null
						}
					/>
				</Grid>
				<Grid item sm={2}>
					<TextField
						variant="outlined"
						fullWidth
						size="small"
						label={t(langAdminEmailNotifications.filter_keywords.tag)}
						value={filters?.documentKeywords}
						onChange={(event) =>
							setFilters((prevData) => ({ ...prevData, documentKeywords: event.target.value }))
						}
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: '45px',
							},
							'& .MuiInputBase-input': {
								paddingLeft: '10px',
							},
							marginBottom: '15px',
						}}
						InputProps={{
							startAdornment: <AppCustomIcon tag="search" />,
						}}
					/>
				</Grid>
				{!hideFilters || (hideFilters && !hideFilters.includes('contactKeywords')) ? (
					<Grid item sm={2}>
						<TextField
							variant="outlined"
							fullWidth
							size="small"
							label={t(langAdminEmailNotifications.filter_contact.tag)}
							value={filters?.contactKeywords}
							onChange={(event) =>
								setFilters((prevData) => ({ ...prevData, contactKeywords: event.target.value }))
							}
							sx={{
								'& .MuiOutlinedInput-root': {
									borderRadius: '45px',
								},
								'& .MuiInputBase-input': {
									paddingLeft: '10px',
								},
								marginBottom: '15px',
							}}
							InputProps={{
								startAdornment: <AppCustomIcon tag="search" />,
							}}
						/>
					</Grid>
				) : (
					<></>
				)}
			</Grid>
		</Box>
	);
};

export default CAdminEmailNotificationFilters;
